// src/Uploader.ts
import {
  StorageTarget
} from "@wix/ambassador-ctp-app-cloud-deployer-service-v1-app-cloud-deployer/types";
import { createPresignedUrls } from "@wix/ambassador-ctp-app-cloud-deployer-service-v1-app-cloud-deployer/http";
import { createReadStream, statSync } from "fs";
import pRetry from "p-retry";
import FormData from "form-data";
import { globby } from "globby";
import path from "path";
var Uploader = class {
  constructor(httpClient, eventListeners) {
    this.httpClient = httpClient;
    this.eventListeners = eventListeners;
  }
  async upload({
    appId,
    folderToUpload,
    validateHashes = true,
    storageTarget = StorageTarget.SERVER_DEFAULT
  }) {
    const filesToUpload = await this.getFilesToUpload(folderToUpload);
    const filesRelativeToAbsolutePathMap = this.createRelativePathToAbsolutePathMap({
      folderToUpload,
      filesToUpload
    });
    if (validateHashes) {
      const invalidFiles = filesToUpload.filter((absolutePath) => {
        const fileName = path.basename(absolutePath);
        return !fileName.includes(".ch.");
      });
      if (invalidFiles.length > 0) {
        throw new Error(`Invalid file(s) found: ${invalidFiles.join(", ")}`);
      }
    }
    const { filePresignedUrls, serveUrl } = await this.createPresignedUrls(
      appId,
      Object.keys(filesRelativeToAbsolutePathMap),
      storageTarget
    );
    const promises = [];
    for (const filePresignedUrl of filePresignedUrls) {
      const { uploadUrl, fields, fileName } = filePresignedUrl;
      this.eventListeners?.onStart?.(fileName);
      const filePathToUpload = filesRelativeToAbsolutePathMap[fileName];
      const fileStream = createReadStream(filePathToUpload, {
        // Lower the highWaterMark from the default 64KB to 16KB to reduce memory usage - https://wix.slack.com/archives/C05225F25JB/p1730206351383649
        highWaterMark: 16 * 1024
      });
      promises.push(
        this.uploadFile(fileName, uploadUrl, fields, fileStream).catch(
          (err) => {
            this.eventListeners?.onFailed?.(fileName, err);
            throw err;
          }
        )
      );
    }
    try {
      await Promise.all(promises);
    } catch (e) {
      throw new Error(`Failed to upload one or more files: ${e.message}`);
    }
    if (!serveUrl) {
      throw new Error("Failed to get serve URL");
    }
    return {
      serveUrl
    };
  }
  async getUploadStats(folderToUpload) {
    const filesToUpload = await this.getFilesToUpload(folderToUpload);
    const totalSize = filesToUpload.reduce((total, filePath) => {
      const stats = statSync(filePath);
      return total + stats.size;
    }, 0);
    return {
      fileCount: filesToUpload.length,
      totalSizeKb: Math.trunc(totalSize / 1024)
    };
  }
  async getFilesToUpload(folderToUpload) {
    return globby("**/*", {
      cwd: folderToUpload,
      absolute: true
    });
  }
  createRelativePathToAbsolutePathMap({
    filesToUpload,
    folderToUpload
  }) {
    const map = {};
    for (const absoluteFilePath of filesToUpload) {
      const relativeFilePath = path.relative(folderToUpload, absoluteFilePath).replaceAll("\\", "/");
      map[relativeFilePath] = absoluteFilePath;
    }
    return map;
  }
  async createPresignedUrls(appId, fileNames, storageTarget) {
    const { data } = await pRetry(
      async () => this.httpClient.request(
        createPresignedUrls({
          appId,
          fileNames,
          storageTarget
        })
      ),
      {
        retries: 3
      }
    );
    return data;
  }
  async uploadFile(fileName, url, fields, fileStream) {
    const formData = this.buildFormData(fields, fileName, fileStream);
    await pRetry(
      async () => {
        await this.submitForm(url, formData);
      },
      { retries: 3 }
    );
    this.eventListeners?.onUploaded?.(fileName);
  }
  buildFormData(fields, filename, fileStream) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", fileStream, {
      filename
    });
    return formData;
  }
  async submitForm(url, formData) {
    await new Promise((resolve, reject) => {
      let data = "";
      formData.submit(url, (err, res) => {
        if (err !== null) {
          reject(err);
          return;
        }
        res.on("data", (chunk) => {
          data += chunk.toString();
        });
        res.on("end", () => {
          if ([200, 204].includes(res.statusCode)) {
            resolve();
          } else {
            reject(data);
          }
        });
      });
    });
  }
};

// src/index.ts
import { StorageTarget as StorageTarget2 } from "@wix/ambassador-ctp-app-cloud-deployer-service-v1-app-cloud-deployer/types";
export {
  StorageTarget2 as StorageTarget,
  Uploader
};
